// The softphone recording test (docs/ACTIONS-2026-09-17.md B1; D75, M9, M11): one page under Admin
// at /admin/twilio-test, for the one person the API admits. A number to call, a Call button that
// asks the API for a Voice access token and places the call from the browser through the Twilio
// Voice JavaScript SDK, a Hang up button, the call's own events as they happen, and what Twilio
// says about the recording once its callback has landed on the API.
//
// Three rules this page holds:
//   * The number is typed here, sent to Twilio as the call's To parameter, and kept nowhere: not in
//     storage, not in the URL, not in the API's log. Reloading the page forgets it.
//   * Every state is the API's or the SDK's own words, never a guess: "Twilio has not called back
//     yet" and "Twilio called back and was refused" are different states and the page says which.
//   * Nothing here is the softphone (B8). It is the test that decides whether the softphone can be
//     built at all, and it is deleted or kept as a diagnostic when B8 starts.
//
// The SDK is loaded when Call is first pressed, so every other page stays the size it was.

import { Chip } from '../parts/primitives.js';
import { ActionButton, ActionInput } from '../parts/ActionButton.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Call, Device } from '@twilio/voice-sdk';
import { ApiError, Unauthenticated } from '../api.js';
import { E164, tidyNumber, twilioTest, type TestCall, type TestStatusView } from './api.js';
import './twilio-test.css';

type Load = { kind: 'loading' } | { kind: 'ready'; view: TestStatusView } | { kind: 'error'; message: string };
type Phase =
  | { kind: 'idle' }
  | { kind: 'minting' }
  | { kind: 'connecting' }
  | { kind: 'ringing' }
  | { kind: 'up'; since: number }
  | { kind: 'ended'; why: string }
  | { kind: 'failed'; message: string };

interface Line { at: string; text: string }

const POLL_MS = 2000;
const AFTER_CALL_MS = 120_000;

const clock = () => new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
function words(e: unknown): string {
  if (e instanceof Unauthenticated) throw e;
  if (e === undefined || e === null) return 'the SDK gave no reason';
  if (e instanceof ApiError) return e.message;
  if (e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string') {
    const err = e as { message: string; code?: number; name?: string };
    // A TwilioError's message already carries its name and code ("AccessTokenInvalid (20101): …").
    if (err.name && err.message.startsWith(err.name)) return err.message;
    return `${err.name ?? 'Error'}${err.code !== undefined ? ` ${err.code}` : ''}: ${err.message}`;
  }
  return String(e);
}

export function TwilioTest() {
  const [status, setStatus] = useState<Load>({ kind: 'loading' });
  const [number, setNumber] = useState('');
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [lines, setLines] = useState<Line[]>([]);
  const [callerId, setCallerId] = useState<string | null>(null);
  const [callSid, setCallSid] = useState<string | null>(null);
  const [watchUntil, setWatchUntil] = useState<number>(0);
  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<Call | null>(null);
  // The SDK reports the reason on the device's error event and rejects connect() with nothing
  // (seen in the check of 2026-09-17: "Failed: undefined" for an invalid token), so the last
  // device error is kept to be the failure's words.
  const lastDeviceError = useRef<string | null>(null);

  const say = useCallback((text: string) => setLines((l) => [...l, { at: clock(), text }].slice(-40)), []);

  const refresh = useCallback(() => {
    twilioTest.status()
      .then((view) => { setStatus({ kind: 'ready', view }); setCallerId(view.callerId); })
      .catch((e: unknown) => setStatus({ kind: 'error', message: words(e) }));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll while a call is being placed or is up, and for two minutes after it ends: the recording's
  // "completed" callback arrives after the call, not during it.
  const active = phase.kind === 'minting' || phase.kind === 'connecting' || phase.kind === 'ringing' || phase.kind === 'up';
  useEffect(() => {
    if (!active && Date.now() > watchUntil) return;
    const t = window.setInterval(() => {
      refresh();
      if (!active && Date.now() > watchUntil) window.clearInterval(t);
    }, POLL_MS);
    return () => window.clearInterval(t);
  }, [active, watchUntil, refresh]);

  useEffect(() => () => { deviceRef.current?.destroy(); }, []);

  const tidy = tidyNumber(number);
  const numberOk = E164.test(tidy);
  const ready = status.kind === 'ready' ? status.view.readiness : null;
  const canCall = !!ready?.canCall && numberOk && !active;

  async function call() {
    if (!canCall) return;
    setLines([]);
    setCallSid(null);
    lastDeviceError.current = null;
    setPhase({ kind: 'minting' });
    say('asking the API for a Voice access token');
    try {
      const minted = await twilioTest.token();
      setCallerId(minted.callerId);
      say(`token for ${minted.identity}, good until ${new Date(minted.expiresAt).toLocaleTimeString('en-GB')}; the phone will show ${minted.callerId}`);
      const { Device: SdkDevice } = await import('@twilio/voice-sdk');
      deviceRef.current?.destroy();
      const device = new SdkDevice(minted.token, { logLevel: 'warn', closeProtection: true });
      deviceRef.current = device;
      device.on('error', (e: unknown) => { lastDeviceError.current = words(e); say(`device error: ${words(e)}`); });
      setPhase({ kind: 'connecting' });
      say('connecting to Twilio (the browser will ask for the microphone the first time)');
      const c = await device.connect({ params: { To: tidy } });
      callRef.current = c;
      c.on('ringing', (hasEarlyMedia: boolean) => { setPhase({ kind: 'ringing' }); say(`ringing${hasEarlyMedia ? ' (early media)' : ''}`); });
      c.on('accept', () => {
        setPhase({ kind: 'up', since: Date.now() });
        const sid = c.parameters['CallSid'] ?? null;
        setCallSid(sid);
        say(`answered${sid ? `; call ${sid}` : ''}; say a few words on both ends, then Hang up`);
      });
      c.on('disconnect', () => {
        setPhase({ kind: 'ended', why: 'the call ended' });
        setWatchUntil(Date.now() + AFTER_CALL_MS);
        say('disconnected; waiting for Twilio\'s recording callback (up to two minutes)');
        device.destroy();
        deviceRef.current = null;
        callRef.current = null;
        refresh();
      });
      c.on('cancel', () => { setPhase({ kind: 'ended', why: 'the call was cancelled before it was answered' }); setWatchUntil(Date.now() + AFTER_CALL_MS); say('cancelled'); refresh(); });
      c.on('reject', () => { setPhase({ kind: 'ended', why: 'the call was rejected' }); setWatchUntil(Date.now() + AFTER_CALL_MS); say('rejected'); refresh(); });
      c.on('error', (e: unknown) => { setPhase({ kind: 'failed', message: words(e) }); setWatchUntil(Date.now() + AFTER_CALL_MS); say(`call error: ${words(e)}`); refresh(); });
    } catch (e) {
      const message = (e === undefined || e === null) && lastDeviceError.current ? lastDeviceError.current : words(e);
      setPhase({ kind: 'failed', message });
      setWatchUntil(Date.now() + AFTER_CALL_MS);
      say(`could not place the call: ${message}`);
      deviceRef.current?.destroy();
      deviceRef.current = null;
    }
  }

  function hangUp() {
    const c = callRef.current;
    if (c) { say('hanging up'); c.disconnect(); }
    else { deviceRef.current?.disconnectAll(); }
  }

  const view = status.kind === 'ready' ? status.view : null;
  const latest: TestCall | null = view?.calls[0] ?? null;

  return (
    <div className="twt">
      <h1>Softphone recording test</h1>
      <p className="twt-sub">Does a call placed from the Twilio Voice SDK in this browser get recorded? The first of the two claims the softphone rests on. Nothing here is the softphone.</p>

      <section className="panel twt-panel">
        <h2>What is in place</h2>
        {status.kind === 'loading' ? <p className="note">Asking the API…</p> : null}
        {status.kind === 'error' ? <p className="error">{status.message} <ActionButton reading type="button" className="dotted" onClick={refresh}>Try again</ActionButton></p> : null}
        {ready ? (
          <ul className="twt-checks">
            <Check ok={ready.credentials} label="Account SID and API key on the droplet" />
            <Check ok={ready.number} label={`Twilio number${callerId ? ` (${callerId})` : ''}`} />
            <Check ok={ready.twimlApp} label="TwiML App (twilio-record-probe setup, then TWILIO_TWIML_APP_SID placed)" />
            <Check ok={ready.callbackVerification} label="Callback verification (TWILIO_AUTH_TOKEN placed)" />
          </ul>
        ) : null}
        {ready && ready.missing.length > 0 ? <p className="note">Missing or malformed: {ready.missing.join(', ')}. The API restarts to read a value placed after it started.</p> : null}
      </section>

      <section className="panel twt-panel">
        <h2>Place the call</h2>
        <p className="rule">Type the number the call should go to, in international form. It is sent to Twilio as the number to dial and kept nowhere; the phone will show the Twilio number as the caller.</p>
        <div className="twt-row">
          <label className="field twt-number">
            <ActionInput type="tel" inputMode="tel" autoComplete="off" placeholder="+447700900123" value={number} onChange={(e) => setNumber(e.target.value)} disabled={active} aria-label="Number to call" />
          </label>
          <ActionButton type="button" className="btn" onClick={() => void call()} disabled={!canCall}>Call</ActionButton>
          <ActionButton type="button" className="btn quiet" onClick={hangUp} disabled={!active || phase.kind === 'minting'}>Hang up</ActionButton>
        </div>
        {number && !numberOk ? <p className="note">Not international form yet: + then the country code and number, no spaces.</p> : null}
        {ready && !ready.canCall ? <p className="note">Call is off until the credentials, the number and the TwiML App are all in place.</p> : null}
        <p className="twt-phase"><PhaseWords phase={phase} /></p>
        {lines.length > 0 ? (
          <ol className="twt-lines">
            {lines.map((l, i) => <li key={i}><span className="twt-at">{l.at}</span>{l.text}</li>)}
          </ol>
        ) : null}
      </section>

      <section className="panel twt-panel">
        <h2>What Twilio said about the recording</h2>
        {view && view.refusals.count > 0 ? (
          <p className="error">Twilio called back {view.refusals.count === 1 ? 'once' : `${view.refusals.count} times`} and was refused. Last, at {view.refusals.last ? new Date(view.refusals.last.at).toLocaleTimeString('en-GB') : '?'} on {view.refusals.last?.route}: {view.refusals.last?.reason}</p>
        ) : null}
        {!latest ? <p className="note">{active ? 'Waiting for Twilio to reach the API.' : 'No call has reached the API yet.'}</p> : (
          <div className="twt-calls">
            {view!.calls.slice(0, 5).map((c) => (
              <div key={c.callSid} className={c.callSid === (callSid ?? latest.callSid) ? 'twt-call on' : 'twt-call'}>
                <div className="twt-call-head">
                  <strong>Call {c.callSid}</strong>
                  <Chip>{c.startedAt ? `answered by the API at ${new Date(c.startedAt).toLocaleTimeString('en-GB')}` : 'callback only: this API did not answer its start'}</Chip>
                </div>
                {c.recordings.length === 0 ? <p className="note">No recording callback yet.</p> : (
                  <dl className="kv">
                    {c.recordings.map((r, i) => (
                      <RecordingRow key={`${r.recordingSid}-${i}`} r={r} />
                    ))}
                  </dl>
                )}
              </div>
            ))}
          </div>
        )}
        <p className="note">The record for DISCOVERY is Twilio's own: on the droplet, <code>twilio-record-probe result</code> reads the Recordings API and prints the SID, duration and channels, or the failure verbatim.</p>
      </section>
    </div>
  );
}

function Check({ ok, label }: { ok: boolean; label: string }) {
  return <li className={ok ? 'twt-check ok' : 'twt-check'}><span className="twt-mark" aria-hidden="true">{ok ? '✓' : '·'}</span>{label}<span className="twt-state">{ok ? 'in place' : 'not yet'}</span></li>;
}

function PhaseWords({ phase }: { phase: Phase }) {
  switch (phase.kind) {
    case 'idle': return <>Ready.</>;
    case 'minting': return <>Getting a token…</>;
    case 'connecting': return <>Connecting…</>;
    case 'ringing': return <>Ringing the number…</>;
    case 'up': return <>Call in progress since {new Date(phase.since).toLocaleTimeString('en-GB')}.</>;
    case 'ended': return <>Ended: {phase.why}.</>;
    case 'failed': return <span className="twt-bad">Failed: {phase.message}</span>;
  }
}

function RecordingRow({ r }: { r: TestCall['recordings'][number] }) {
  return (
    <>
      <dt>Recording</dt><dd>{r.recordingSid}</dd>
      <dt>Status</dt><dd><Chip className={r.status === 'completed' ? 'reg' : r.status === 'absent' ? 'warm' : ''}>{r.status}</Chip> at {new Date(r.receivedAt).toLocaleTimeString('en-GB')}{r.errorCode ? ` (error ${r.errorCode})` : ''}</dd>
      <dt>Duration</dt><dd>{r.durationSeconds === null ? '—' : `${r.durationSeconds} s`}</dd>
      <dt>Channels</dt><dd>{r.channels === null ? '—' : r.channels === 2 ? '2 (both legs, separately)' : String(r.channels)}</dd>
      <dt>Source</dt><dd>{r.source ?? '—'}</dd>
    </>
  );
}
